import React, { useEffect, useMemo, useState } from "react";
import {
    Button,
    Modal,
    TextInput,
    Select,
    Group,
    Box,
    Badge,
    ActionIcon,
    Checkbox,
    Textarea,
    Title,
} from "@mantine/core";
import { IconEdit, IconTrash, IconPlus } from "@tabler/icons-react";
import CommonTable from "../components/CommonTable";

const BASE =
    process.env.REACT_APP_API_URL || "https://news-portal-l6pf.onrender.com";
const API_URL = `${BASE}/api/users`;

const User = () => {
    const [users, setUsers] = useState([]);
    const [opened, setOpened] = useState(false);
    const [editId, setEditId] = useState(null);

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        role: "reporter",
        designation: "",
        department: "",
        bio: "",
        experience: "",
        isVerified: false,
        canPublish: false,
        status: "active",
    });

    const fetchUsers = async () => {
        try {
            const res = await fetch(API_URL);
            const data = await res.json();
            setUsers(data?.data || []);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const openModal = (user = null) => {
        if (user) {
            setEditId(user._id);
            setFormData(user);
        } else {
            setEditId(null);
            setFormData({
                name: "",
                email: "",
                phone: "",
                role: "reporter",
                designation: "",
                department: "",
                bio: "",
                experience: "",
                isVerified: false,
                canPublish: false,
                status: "active",
            });
        }
        setOpened(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const method = editId ? "PUT" : "POST";
        const url = editId ? `${API_URL}/${editId}` : API_URL;

        await fetch(url, {
            method,
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(formData),
        });

        fetchUsers();
        setOpened(false);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this user?")) return;
        await fetch(`${API_URL}/${id}`, { method: "DELETE" });
        fetchUsers();
    };

    const columns = useMemo(
        () => [
            { accessorKey: "name", header: "Name", size: 160 },
            { accessorKey: "email", header: "Email", size: 220 },
            { accessorKey: "phone", header: "Phone", size: 130 },

            {
                accessorKey: "role",
                header: "Role",
                size: 120,
                Cell: ({ cell }) => (
                    <Badge variant="light" color="blue">
                        {cell.getValue()}
                    </Badge>
                ),
            },

            { accessorKey: "designation", header: "Designation", size: 180 },
            { accessorKey: "department", header: "Department", size: 150 },

            {
                accessorKey: "experience",
                header: "Experience",
                size: 100,
            },

            {
                accessorKey: "isVerified",
                header: "Verified",
                size: 120,
                Cell: ({ cell }) =>
                    cell.getValue() ? (
                        <Badge color="green">Yes</Badge>
                    ) : (
                        <Badge color="gray">No</Badge>
                    ),
            },

            {
                accessorKey: "canPublish",
                header: "Publish",
                size: 120,
                Cell: ({ cell }) =>
                    cell.getValue() ? (
                        <Badge color="blue">Allowed</Badge>
                    ) : (
                        <Badge color="red">Restricted</Badge>
                    ),
            },

            {
                accessorKey: "status",
                header: "Status",
                size: 120,
                Cell: ({ cell }) =>
                    cell.getValue() === "active" ? (
                        <Badge color="green">Active</Badge>
                    ) : (
                        <Badge color="red">Inactive</Badge>
                    ),
            },

            {
                header: "Actions",
                accessorKey: "Actions",
                size: 130,
                Cell: ({ row }) => (
                    <Group gap="xs">
                        <ActionIcon
                            variant="light"
                            color="blue"
                            onClick={() => openModal(row.original)}
                        >
                            <IconEdit size={16} />
                        </ActionIcon>
                        <ActionIcon
                            variant="light"
                            color="red"
                            onClick={() => handleDelete(row.original._id)}
                        >
                            <IconTrash size={16} />
                        </ActionIcon>
                    </Group>
                ),
            },
        ],
        []
    );

    return (
        <Box p="lg">
            <Group justify="space-between" mb="lg">
                <Title order={3}>User Management</Title>
                <Button
                    leftSection={<IconPlus size={16} />}
                    onClick={() => openModal()}
                >
                    Add User
                </Button>
            </Group>

            <CommonTable columns={columns} data={users} fileName="news-users" />

            <Modal
                opened={opened}
                onClose={() => setOpened(false)}
                title={editId ? "Edit User" : "Add User"}
                size="lg"
                centered
            >
                <form onSubmit={handleSubmit}>
                    <TextInput label="Name" required mb="md"
                        value={formData.name}
                        onChange={(e) =>
                            setFormData({ ...formData, name: e.target.value })
                        }
                    />

                    <TextInput label="Email" required mb="md"
                        value={formData.email}
                        onChange={(e) =>
                            setFormData({ ...formData, email: e.target.value })
                        }
                    />

                    <TextInput label="Phone" mb="md"
                        value={formData.phone}
                        onChange={(e) =>
                            setFormData({ ...formData, phone: e.target.value })
                        }
                    />

                    <Select label="Role" mb="md"
                        data={[
                            { value: "admin", label: "Admin" },
                            { value: "editor", label: "Editor" },
                            { value: "reporter", label: "Reporter" },
                            { value: "user", label: "User" },
                        ]}
                        value={formData.role}
                        onChange={(value) =>
                            setFormData({ ...formData, role: value })
                        }
                    />

                    <TextInput label="Designation" mb="md"
                        value={formData.designation}
                        onChange={(e) =>
                            setFormData({ ...formData, designation: e.target.value })
                        }
                    />

                    <TextInput label="Department" mb="md"
                        value={formData.department}
                        onChange={(e) =>
                            setFormData({ ...formData, department: e.target.value })
                        }
                    />

                    <TextInput label="Experience" type="number" mb="md"
                        value={formData.experience}
                        onChange={(e) =>
                            setFormData({ ...formData, experience: e.target.value })
                        }
                    />

                    <Checkbox
                        label="Verified Author"
                        mb="sm"
                        checked={formData.isVerified}
                        onChange={(e) =>
                            setFormData({
                                ...formData,
                                isVerified: e.currentTarget.checked,
                            })
                        }
                    />

                    <Checkbox
                        label="Can Publish"
                        mb="md"
                        checked={formData.canPublish}
                        onChange={(e) =>
                            setFormData({
                                ...formData,
                                canPublish: e.currentTarget.checked,
                            })
                        }
                    />

                    <Select
                        label="Status"
                        mb="lg"
                        data={[
                            { value: "active", label: "Active" },
                            { value: "inactive", label: "Inactive" },
                        ]}
                        value={formData.status}
                        onChange={(value) =>
                            setFormData({ ...formData, status: value })
                        }
                    />

                    <Group justify="flex-end">
                        <Button type="submit">
                            {editId ? "Update" : "Create"}
                        </Button>
                    </Group>
                </form>
            </Modal>
        </Box>
    );
};

export default User;